import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { toast } from "react-toastify";
import EmployeeFiltersComponent from "@/features/employee/components/EmployeeFilters";
import EmployeeTable from "@/features/employee/components/EmployeeTable";
import UserPagination from "@/features/user/components/UserPagination";
import Modal from "@/features/shared/components/Modal/Modal";
import EmployeeForm from "@/features/employee/components/EmployeeForm";
import useEmployees from "@/features/employee/hooks/useEmployees";
import { fetchUsers } from "@/features/user/services/userService";
import { fetchAllContractTypes } from "@/features/contractType/services/contractTypeService";
import EmployeeAttachmentsModal from "@/features/employee/components/EmployeeAttachmentsModal";
const defaultFilters = {
    first_name: "",
    last_name: "",
    email: "",
    contract_type_code: undefined,
};
const PersonnelPage = () => {
    const [filters, setFilters] = useState({ ...defaultFilters });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [users, setUsers] = useState([]);
    const [contractTypes, setContractTypes] = useState([]);
    const [formVisible, setFormVisible] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [attachmentsEmployee, setAttachmentsEmployee] = useState(null);
    const { employees, meta, loading, error, handleCreateEmployee, handleUpdateEmployee, handleDeleteEmployee, refetchEmployees } = useEmployees(currentPage, filters, rowsPerPage);
    useEffect(() => {
        const loadUsers = async () => {
            try {
                const response = await fetchUsers(undefined, undefined, true, 200);
                setUsers(response.users ?? []);
            }
            catch (err) {
                toast.error(err.message ?? "Impossible de charger les utilisateurs");
            }
        };
        const loadContractTypes = async () => {
            try {
                const response = await fetchAllContractTypes();
                setContractTypes(response);
            }
            catch (err) {
                toast.error(err.message ?? "Impossible de charger les types de contrat");
            }
        };
        void loadUsers();
        void loadContractTypes();
    }, []);
    const totalPages = useMemo(() => meta?.totalPages ?? 1, [meta]);
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
        }));
        setCurrentPage(1);
    };
    const handleResetFilters = () => {
        setFilters({ ...defaultFilters });
        setCurrentPage(1);
    };
    const openCreateForm = () => {
        setSelectedEmployee(null);
        setFormVisible(true);
    };
    const handleEdit = (employee) => {
        setSelectedEmployee(employee);
        setFormVisible(true);
    };
    const closeForm = () => {
        setFormVisible(false);
        setSelectedEmployee(null);
    };
    const handleSubmit = async (payload) => {
        try {
            if (selectedEmployee) {
                await handleUpdateEmployee(selectedEmployee.id, payload);
                toast.success("Employé mis à jour");
            }
            else {
                await handleCreateEmployee(payload);
                toast.success("Employé créé");
            }
            closeForm();
        }
        catch (err) {
            toast.error(err.message ?? "Impossible d'enregistrer l'employé");
        }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cet employé ?"))
            return;
        try {
            await handleDeleteEmployee(id);
            toast.success("Employé supprimé");
        }
        catch (err) {
            toast.error(err.message ?? "Suppression impossible");
        }
    };
    const openAttachments = (employee) => setAttachmentsEmployee(employee);
    const closeAttachments = () => setAttachmentsEmployee(null);
    return (_jsxs("div", { className: "p-4 bg-gray-50 min-h-screen", children: [_jsxs("div", { className: "flex col-auto gap-4 mb-2", children: [_jsx("h4", { className: "text-xl font-bold", children: "Gestion des contrats des employ\u00E9s" }), _jsx("button", { onClick: openCreateForm, title: "Ajouter un employ\u00E9", className: "rounded", children: _jsx(IoMdAdd, { className: "bg-blue-500 text-white hover:bg-blue-600" }) })] }), _jsx(EmployeeFiltersComponent, { filters: filters, contractTypes: contractTypes, onChange: handleFilterChange, onReset: handleResetFilters }), loading && _jsx("div", { className: "p-4", children: "Chargement..." }), error && (_jsxs("div", { className: "p-4 text-red-500", children: ["Erreur lors du chargement des employ\u00E9s : ", error, _jsx("button", { onClick: () => refetchEmployees(), className: "ml-4 bg-blue-500 text-white px-2 py-1 rounded", children: "R\u00E9essayer" })] })), !loading && !error && (_jsxs(_Fragment, { children: [_jsx(EmployeeTable, { employees: employees, users: users, onEdit: handleEdit, onDelete: handleDelete, onManageAttachments: openAttachments }), meta && (_jsx(UserPagination, { currentPage: meta.page ?? 1, totalPages: totalPages, limit: meta.limit ?? rowsPerPage, onPageChange: setCurrentPage, onLimitChange: (value) => {
                            setRowsPerPage(value);
                            setCurrentPage(1);
                        } }))] })), formVisible && (_jsx(Modal, { onClose: closeForm, children: _jsx(EmployeeForm, { initialData: selectedEmployee ?? undefined, users: users, contractTypes: contractTypes, onSubmit: handleSubmit, onCancel: closeForm }) })), attachmentsEmployee && (_jsx(EmployeeAttachmentsModal, { employee: attachmentsEmployee, onClose: closeAttachments }))] }));
};
export default PersonnelPage;
